import localforage from "localforage";
import { PostHog } from "posthog-js";

// Synchronous in-memory guard prevents double-fire when two async trackOnce
// calls race before either localforage.setItem completes (e.g. during rapid
// component transitions).
const _firedInSession = new Set<string>();
const _firedInBrowserSession = new Set<string>();
const BROWSER_SESSION_TRACKING_PREFIX = "tracking-session-";

// Test utility: clears the in-memory tracking set
export const clearTrackedEvents = () => {
  _firedInSession.clear();
};

export const clearSessionTrackedEvents = () => {
  _firedInBrowserSession.clear();

  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(BROWSER_SESSION_TRACKING_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // The in-memory guard still provides once-per-page-session behavior when
    // browser storage is unavailable.
  }
};

export const trackOncePerSession = (
  posthog: PostHog | null | undefined,
  key: string,
  values = {} as Record<string, unknown>,
  uniqueKey?: string
): void => {
  if (posthog == null) return;

  const trackingKey = `${BROWSER_SESSION_TRACKING_PREFIX}${key}${
    uniqueKey ? `-${uniqueKey}` : ""
  }`;
  if (_firedInBrowserSession.has(trackingKey)) return;

  try {
    if (sessionStorage.getItem(trackingKey)) return;
  } catch {
    // Continue with the in-memory guard when browser storage is unavailable.
  }

  _firedInBrowserSession.add(trackingKey);
  posthog.capture(key, values);

  try {
    sessionStorage.setItem(trackingKey, "true");
  } catch {
    // The event is already guarded in memory for the current page session.
  }
};

export const trackOnce = async (
  posthog: PostHog | null | undefined,
  key: string,
  values = {} as Record<string, unknown>,
  uniqueKey?: string
): Promise<void> => {
  if (posthog == null) return;
  const trackingKey = uniqueKey
    ? `tracking-${key}-${uniqueKey}`
    : `tracking-${key}`;
  if (_firedInSession.has(trackingKey)) return;
  _firedInSession.add(trackingKey);
  try {
    if (await localforage.getItem(trackingKey)) return;
    posthog.capture(key, values);
    await localforage.setItem(trackingKey, true);
  } catch (error) {
    _firedInSession.delete(trackingKey);
    console.error("Error tracking event:", error);
  }
};
